import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ArrowLeft, 
  Shield, 
  MessageSquare
} from 'lucide-react';

interface BattleArenaProps {
  questions: any[];
  chapterName: string;
  subjectName: string;
  onClose: () => void;
  isDarkMode?: boolean;
  userProfile?: {
    name: string;
    avatar: string;
  } | null;
}

interface Classmate {
  name: string;
  avatar: string;
  avatarColor: string;
  avatarLetter: string;
  batch: string;
  accuracy: string;
  activeTime?: string;
  online: boolean;
}

const classmatesList: Classmate[] = [
  { name: "রাহুল আহমেদ", avatar: "👨‍🎓", avatarColor: "from-cyan-400 to-blue-500", avatarLetter: "রা", batch: "SSC 2027", accuracy: "88%", online: true, activeTime: "অনলাইন" },
  { name: "সাকিব হাসান", avatar: "👨‍🏫", avatarColor: "from-purple-500 to-indigo-500", avatarLetter: "সা", batch: "SSC 2026", accuracy: "92%", online: true, activeTime: "অনলাইন" },
  { name: "ফারহান তানভীর", avatar: "👨‍💻", avatarColor: "from-pink-500 to-rose-500", avatarLetter: "ফা", batch: "HSC 2026", accuracy: "85%", online: true, activeTime: "অনলাইন" },
  { name: "তাসনিম আরা", avatar: "🧑‍🚀", avatarColor: "from-amber-400 to-orange-500", avatarLetter: "তা", batch: "SSC 2027", accuracy: "94%", online: true, activeTime: "অনলাইন" },
  { name: "মেহেদী হাসান", avatar: "🧑‍🎨", avatarColor: "from-emerald-400 to-teal-500", avatarLetter: "মে", batch: "SSC 2028", accuracy: "81%", online: true, activeTime: "অনলাইন" },
  { name: "আরিফ রহমান", avatar: "🧑‍🚒", avatarColor: "from-blue-500 to-indigo-600", avatarLetter: "আ", batch: "HSC 2027", accuracy: "79%", online: false, activeTime: "৫ মিনিট আগে" },
  { name: "আফরোজা সুলতানা", avatar: "🕵️", avatarColor: "from-fuchsia-500 to-pink-600", avatarLetter: "আ", batch: "SSC 2026", accuracy: "87%", online: false, activeTime: "১২ মিনিট আগে" },
  { name: "ফাহমিদা চৌধুরী", avatar: "🧑‍⚕️", avatarColor: "from-rose-400 to-orange-500", avatarLetter: "ফা", batch: "SSC 2027", accuracy: "83%", online: false, activeTime: "৮ মিনিট আগে" },
  { name: "জুবায়ের মাহমুদ", avatar: "🧑‍🔧", avatarColor: "from-teal-400 to-emerald-600", avatarLetter: "জু", batch: "HSC 2026", accuracy: "90%", online: false, activeTime: "২০ মিনিট আগে" },
  { name: "নাবিল আহমেদ", avatar: "👨‍🎓", avatarColor: "from-violet-500 to-purple-600", avatarLetter: "না", batch: "SSC 2027", accuracy: "76%", online: false, activeTime: "১৫ মিনিট আগে" },
  { name: "আফিয়া আক্তার", avatar: "👨‍🏫", avatarColor: "from-pink-400 to-red-500", avatarLetter: "আ", batch: "SSC 2028", accuracy: "84%", online: true, activeTime: "অনলাইন" },
  { name: "তানজিম শাকিল", avatar: "👨‍💻", avatarColor: "from-sky-400 to-blue-600", avatarLetter: "তা", batch: "HSC 2027", accuracy: "89%", online: true, activeTime: "অনলাইন" },
  { name: "নিশাত আনজুম", avatar: "🧑‍🚀", avatarColor: "from-yellow-400 to-amber-500", avatarLetter: "নি", batch: "SSC 2026", accuracy: "91%", online: true, activeTime: "অনলাইন" },
  { name: "সায়মন ইসলাম", avatar: "🧑‍🎨", avatarColor: "from-lime-400 to-green-600", avatarLetter: "সা", batch: "SSC 2027", accuracy: "80%", online: false, activeTime: "২ মিনিট আগে" },
  { name: "শরিফুল ইসলাম", avatar: "🧑‍🚒", avatarColor: "from-emerald-500 to-teal-600", avatarLetter: "শ", batch: "HSC 2026", accuracy: "86%", online: false, activeTime: "৯ মিনিট আগে" },
  { name: "তামান্না ফেরদৌস", avatar: "🕵️", avatarColor: "from-purple-400 to-pink-500", avatarLetter: "তা", batch: "SSC 2028", accuracy: "88%", online: false, activeTime: "৪ মিনিট আগে" },
  { name: "ইশরাক জামান", avatar: "🧑‍⚕️", avatarColor: "from-blue-600 to-cyan-500", avatarLetter: "ই", batch: "SSC 2027", accuracy: "93%", online: false, activeTime: "২৫ মিনিট আগে" },
  { name: "মাইশা রহমান", avatar: "🧑‍🔧", avatarColor: "from-pink-500 to-purple-500", avatarLetter: "মা", batch: "HSC 2027", accuracy: "75%", online: false, activeTime: "৩০ মিনিট আগে" },
  { name: "আদনান সানি", avatar: "👨‍🎓", avatarColor: "from-orange-400 to-red-600", avatarLetter: "আ", batch: "SSC 2026", accuracy: "82%", online: true, activeTime: "অনলাইন" },
  { name: "তাসমিয়া আহমেদ", avatar: "👨‍🏫", avatarColor: "from-indigo-400 to-purple-600", avatarLetter: "তা", batch: "SSC 2027", accuracy: "90%", online: true, activeTime: "অনলাইন" },
  { name: "মুস্তফা কামাল", avatar: "👨‍💻", avatarColor: "from-emerald-400 to-cyan-500", avatarLetter: "মু", batch: "HSC 2026", accuracy: "87%", online: true, activeTime: "অনলাইন" },
  { name: "রাইসা ইসলাম", avatar: "🧑‍🚀", avatarColor: "from-rose-400 to-pink-600", avatarLetter: "রা", batch: "SSC 2028", accuracy: "83%", online: false, activeTime: "৭ মিনিট আগে" },
  { name: "নাবিলা তাবাসসুম", avatar: "🧑‍🎨", avatarColor: "from-amber-500 to-red-500", avatarLetter: "না", batch: "SSC 2026", accuracy: "78%", online: false, activeTime: "১ মিনিট আগে" },
  { name: "আহনাফ হাবিব", avatar: "🧑‍🚒", avatarColor: "from-cyan-500 to-blue-600", avatarLetter: "আ", batch: "HSC 2027", accuracy: "95%", online: false, activeTime: "১৮ মিনিট আগে" },
  { name: "সাদিয়া আফরিন", avatar: "🕵️", avatarColor: "from-pink-400 to-rose-500", avatarLetter: "সা", batch: "SSC 2027", accuracy: "86%", online: true, activeTime: "অনলাইন" },
  { name: "রাফিউল আলম", avatar: "🧑‍⚕️", avatarColor: "from-yellow-500 to-orange-600", avatarLetter: "রা", batch: "SSC 2028", accuracy: "82%", online: true, activeTime: "অনলাইন" },
  { name: "নুসরাত জাহান", avatar: "🧑‍🔧", avatarColor: "from-violet-400 to-indigo-600", avatarLetter: "নু", batch: "HSC 2026", accuracy: "89%", online: false, activeTime: "১১ মিনিট আগে" },
  { name: "ফয়সাল মাহমুদ", avatar: "👨‍🎓", avatarColor: "from-teal-500 to-emerald-500", avatarLetter: "ফ", batch: "SSC 2027", accuracy: "84%", online: false, activeTime: "৩ মিনিট আগে" },
  { name: "তাবাসসুম নেহা", avatar: "👨‍🏫", avatarColor: "from-red-400 to-pink-600", avatarLetter: "তা", batch: "SSC 2026", accuracy: "90%", online: false, activeTime: "১৪ মিনিট আগে" },
  { name: "তাহমিদ চৌধুরী", avatar: "👨‍💻", avatarColor: "from-sky-500 to-indigo-500", avatarLetter: "তা", batch: "HSC 2027", accuracy: "91%", online: true, activeTime: "অনলাইন" },
  { name: "ফারজানা ববি", avatar: "🧑‍🚀", avatarColor: "from-pink-500 to-indigo-500", avatarLetter: "ফা", batch: "SSC 2028", accuracy: "88%", online: true, activeTime: "অনলাইন" },
  { name: "শামসুদ্দিন আহমেদ", avatar: "🧑‍🎨", avatarColor: "from-green-400 to-teal-600", avatarLetter: "শা", batch: "SSC 2027", accuracy: "77%", online: false, activeTime: "১৬ মিনিট আগে" },
  { name: "মারুফ বিল্লাহ", avatar: "🧑‍🚒", avatarColor: "from-purple-500 to-pink-500", avatarLetter: "মা", batch: "HSC 2026", accuracy: "83%", online: false, activeTime: "৬ মিনিট আগে" },
  { name: "নাদিয়া চৌধুরী", avatar: "🕵️", avatarColor: "from-rose-500 to-orange-500", avatarLetter: "না", batch: "SSC 2028", accuracy: "85%", online: false, activeTime: "২২ মিনিট আগে" },
  { name: "জিসান আহমেদ", avatar: "🧑‍⚕️", avatarColor: "from-blue-400 to-indigo-600", avatarLetter: "জি", batch: "HSC 2027", accuracy: "89%", online: true, activeTime: "অনলাইন" }
];

const chatStickers = [
  { text: "খেলা হবে!", emoji: "🔥" },
  { text: "মজা লাগছে!", emoji: "😎" },
  { text: "কেমন দিলাম!!", emoji: "😏" },
  { text: "দারুণ!", emoji: "👏" },
];

const responseMapping: Record<string, { text: string; emoji: string }> = {
  "খেলা হবে!": { text: "দেখা যাবে!", emoji: "⚡" },
  "মজা লাগছে!": { text: "তুমিও ভালো খেলছ!", emoji: "👍" },
  "কেমন দিলাম!!": { text: "পরেরটায় দেখা যাবে!", emoji: "😜" },
  "দারুণ!": { text: "ধন্যবাদ!", emoji: "🙏" },
};

const defaultQuestions = [
  {
    id: 9991,
    question_text: "'মমতাদি' প্রাণপণ চেষ্টায় কী জয় করেছে?",
    options: ["লজ্জা", "সংকোচ", "অভাব", "মন"],
    correct_answer: "1", // 0-indexed option 1 is "সংকোচ"
  },
  {
    id: 9992,
    question_text: "মমতাদির কত বছর বয়সে বিয়ে হয়েছিল?",
    options: ["১২ বছর", "১৩ বছর", "১৫ বছর", "১৮ বছর"],
    correct_answer: "2",
  },
  {
    id: 9993,
    question_text: "মমতাদির বেতন কত টাকা ঠিক হয়েছিল?",
    options: ["১২ টাকা", "১৫ টাকা", "১৮ টাকা", "২০ টাকা"],
    correct_answer: "1",
  },
  {
    id: 9994,
    question_text: "মমতাদির গালে কিসের দাগ ছিল?",
    options: ["আঘাতের দাগ", "জন্মদাগ", "বসন্তের দাগ", "রোদে পোড়া দাগ"],
    correct_answer: "0",
  },
  {
    id: 9995,
    question_text: "মমতাদির বাড়ির পরিবেশ কেমন ছিল?",
    options: ["অত্যন্ত পরিচ্ছন্ন", "বিপর্যস্ত ও মলিন", "কোলাহলপূর্ণ", "বিলাসবহুল"],
    correct_answer: "1",
  },
  {
    id: 9996,
    question_text: "মমতাদি প্রথম দিন কয়টার সময় কাজে এসেছিল?",
    options: ["সকাল ৭টা", "সকাল ৮টা", "সকাল ৯টা", "সকাল ১০টা"],
    correct_answer: "1",
  },
  {
    id: 9997,
    question_text: "মমতাদির স্বামীর চাকরি চলে যাওয়ার পর সংসার কত মাস ধরে অচল ছিল?",
    options: ["দুই মাস", "তিন মাস", "চার মাস", "ছয় মাস"],
    correct_answer: "2",
  },
  {
    id: 9998,
    question_text: "মমতাদি গল্পে লেখক কার সাথে মমতাদির তুলনা করেছেন?",
    options: ["ছায়াময়ী মানসী", "দেবদূত", "গৃহহীন পাখি", "দরিদ্র মাতা"],
    correct_answer: "0",
  },
  {
    id: 9999,
    question_text: "মমতাদির কয়টি সন্তান ছিল?",
    options: ["একটি ছেলে", "দুটি মেয়ে", "একটি ছেলে ও একটি মেয়ে", "কোনো সন্তান ছিল না"],
    correct_answer: "0",
  },
  {
    id: 10000,
    question_text: "মমতাদি গল্পটি কার লেখা?",
    options: ["রবীন্দ্রনাথ ঠাকুর", "কাজী নজরুল ইসলাম", "মানিক বন্দ্যোপাধ্যায়", "শরৎচন্দ্র চট্টোপাধ্যায়"],
    correct_answer: "2",
  }
];

const getAvatarLetter = (name: string): string => {
  if (!name) return "";
  const cleaned = name.trim();
  return cleaned.slice(0, 2);
};

const toBengaliNumber = (num: number | string): string => {
  const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().replace(/\d/g, (d) => bn[parseInt(d)]);
};

export default function BattleArena({
  questions,
  chapterName,
  subjectName,
  onClose,
  isDarkMode = false,
  userProfile = null
}: BattleArenaProps) {
  const [phase, setPhase] = useState<'lobby' | 'matchmaking' | 'invite' | 'vs' | 'game' | 'result'>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return 'result';
        }
      }
    } catch (e) {}
    return 'lobby';
  });
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Invite friend states
  const [inviteTimer, setInviteTimer] = useState(120);
  const [inviteCode, setInviteCode] = useState('');
  
  // Matchmaking states
  const [matchmakingTimer, setMatchmakingTimer] = useState(15);
  const [notifiedCount, setNotifiedCount] = useState(4);
  const [opponent, setOpponent] = useState<Classmate | null>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return parsed.opponent;
        }
      }
    } catch (e) {}
    return null;
  });
  const [lobbyClassmates, setLobbyClassmates] = useState<Classmate[]>([]);

  // Gameplay states
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timer, setTimer] = useState(15);
  const [userSelectedOption, setUserSelectedOption] = useState<number | null>(null);
  const [opponentSelectedOption, setOpponentSelectedOption] = useState<number | null>(null);
  const [userRead, setUserRead] = useState<boolean>(false);
  const [opponentRead, setOpponentRead] = useState<boolean>(false);
  const [userScore, setUserScore] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return parsed.userScore;
        }
      }
    } catch (e) {}
    return 0;
  });
  const [opponentScore, setOpponentScore] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return parsed.opponentScore;
        }
      }
    } catch (e) {}
    return 0;
  });
  const [showFeedback, setShowFeedback] = useState(false);
  
  // Answers history for the final scorecard review
  const [userAnswersHistory, setUserAnswersHistory] = useState<Record<number, number | null>>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return parsed.userAnswersHistory;
        }
      }
    } catch (e) {}
    return {};
  });
  const [opponentAnswersHistory, setOpponentAnswersHistory] = useState<Record<number, number | null>>(() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return parsed.opponentAnswersHistory;
        }
      }
    } catch (e) {}
    return {};
  });
  const [expandedExplanations, setExpandedExplanations] = useState<Record<number, boolean>>({});
  
  // Chat & Stickers states
  const [showStickerTray, setShowStickerTray] = useState(false);
  const [userBubble, setUserBubble] = useState<string | null>(null);
  const [opponentBubble, setOpponentBubble] = useState<string | null>(null);

  // Active question set (10 questions for full duels)
  const activeQuestions = (() => {
    try {
      const saved = localStorage.getItem('shuddhoshikha_battle_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 2 * 60 * 60 * 1000) {
          return parsed.activeQuestions;
        }
      }
    } catch (e) {}
    return questions && questions.length > 0 
      ? questions.slice(0, 10) 
      : defaultQuestions;
  })();

  // Timers references
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const userSelectedOptionRef = useRef<number | null>(null);
  const opponentSelectedOptionRef = useRef<number | null>(null);
  const timerRefVal = useRef(15);

  useEffect(() => {
    userSelectedOptionRef.current = userSelectedOption;
  }, [userSelectedOption]);

  useEffect(() => {
    opponentSelectedOptionRef.current = opponentSelectedOption;
  }, [opponentSelectedOption]);

  useEffect(() => {
    timerRefVal.current = timer;
  }, [timer]);

  // Save results state to localStorage when entering 'result' phase
  useEffect(() => {
    if (phase === 'result' && opponent) {
      const stateToSave = {
        subjectName,
        chapterName,
        opponent,
        userScore,
        opponentScore,
        userAnswersHistory,
        opponentAnswersHistory,
        activeQuestions,
        timestamp: Date.now()
      };
      localStorage.setItem('shuddhoshikha_battle_results', JSON.stringify(stateToSave));
    }
  }, [phase, opponent, userScore, opponentScore, userAnswersHistory, opponentAnswersHistory, activeQuestions, subjectName, chapterName]);

  const formatInviteTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize randomized lobby classmates on phase change or mount
  useEffect(() => {
    if (phase === 'lobby') {
      const shuffled = [...classmatesList].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, 9).map(bot => {
        const isOnline = Math.random() > 0.45;
        const activeMinutes = Math.floor(Math.random() * 28) + 2;
        return {
          ...bot,
          online: isOnline,
          activeTime: isOnline ? "অনলাইন" : `${toBengaliNumber(activeMinutes)} মিনিট আগে`
        };
      });
      setLobbyClassmates(selected);
    }
  }, [phase]);

  // Invite phase effect
  useEffect(() => {
    if (phase !== 'invite') return;

    // Generate random invite code
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let code = 'BATTLE_';
    for (let i = 0; i < 24; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setInviteCode(code);
    setInviteTimer(120);

    const roomKey = `battle_room_${code}`;
    const initialData = {
      creatorName: userProfile?.name || 'Shajjad Islam',
      creatorAvatar: userProfile?.avatar || '👱‍♂️',
      creatorAvatarColor: 'from-cyan-400 to-blue-500',
      creatorAvatarLetter: userProfile?.name ? getAvatarLetter(userProfile.name) : 'শা',
      creatorBatch: 'SSC 2027',
      joined: false,
      joinerName: '',
      joinerAvatar: '',
      joinerAvatarColor: '',
      joinerAvatarLetter: '',
      joinerBatch: ''
    };
    localStorage.setItem(roomKey, JSON.stringify(initialData));

    const interval = setInterval(() => {
      setInviteTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });

      // Poll room status in localStorage to detect when a friend joins
      const stored = localStorage.getItem(roomKey);
      if (stored) {
        try {
          const roomData = JSON.parse(stored);
          if (roomData.joined) {
            clearInterval(interval);
            setOpponent({
              name: roomData.joinerName || 'রাহুল আহমেদ',
              avatar: roomData.joinerAvatar || '🧔',
              avatarColor: roomData.joinerAvatarColor || 'from-pink-500 to-rose-500',
              avatarLetter: roomData.joinerAvatarLetter || 'রা',
              batch: roomData.joinerBatch || 'SSC 2027',
              online: true,
              accuracy: '95%'
            });
            setPhase('vs');
          }
        } catch (e) {
          console.error("Error parsing roomData:", e);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [phase, userProfile]);

  // Matchmaking effect
  useEffect(() => {
    if (phase !== 'matchmaking') return;
    
    // Start with a random number of notified classmates (e.g. 3 to 5)
    setNotifiedCount(Math.floor(Math.random() * 3) + 3);
    
    setMatchmakingTimer(15);
    const interval = setInterval(() => {
      // Randomly increase notified count by 1 (25% chance per second, max 8)
      if (Math.random() < 0.25) {
        setNotifiedCount(prev => Math.min(8, prev + 1));
      }

      setMatchmakingTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // Pick a random bot from the pool excluding the ones in the active lobby to guarantee a new partner
          const lobbyNames = new Set(lobbyClassmates.map(c => c.name));
          const availableOpponents = classmatesList.filter(c => !lobbyNames.has(c.name));
          const chosenBot = availableOpponents[Math.floor(Math.random() * availableOpponents.length)] || classmatesList[0];
          
          setOpponent({
            ...chosenBot,
            name: `${chosenBot.name}`
          });
          setPhase('vs');
          return 0;
        }
        
        // Match early randomly between 3-7 seconds
        if (prev <= 11 && Math.random() < 0.25) {
          clearInterval(interval);
          const lobbyNames = new Set(lobbyClassmates.map(c => c.name));
          const availableOpponents = classmatesList.filter(c => !lobbyNames.has(c.name));
          const chosenPlayer = availableOpponents[Math.floor(Math.random() * availableOpponents.length)] || classmatesList[0];
          setOpponent(chosenPlayer);
          setPhase('vs');
        }
        
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, lobbyClassmates]);

  // Check if joined via shared link to jump straight to VS matchup screen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const battleCode = params.get('battle');
    if (battleCode) {
      const roomKey = `battle_room_${battleCode}`;
      const stored = localStorage.getItem(roomKey);
      
      let creatorName = 'Shajjad Islam';
      let creatorAvatar = '👱‍♂️';
      let creatorAvatarColor = 'from-cyan-400 to-blue-500';
      let creatorAvatarLetter = 'শা';
      let creatorBatch = 'SSC 2027';
      
      if (stored) {
        try {
          const roomData = JSON.parse(stored);
          creatorName = roomData.creatorName || creatorName;
          creatorAvatar = roomData.creatorAvatar || creatorAvatar;
          creatorAvatarColor = roomData.creatorAvatarColor || creatorAvatarColor;
          creatorAvatarLetter = roomData.creatorAvatarLetter || creatorAvatarLetter;
          creatorBatch = roomData.creatorBatch || creatorBatch;
          
          // Notify creator that we joined
          const updatedData = {
            ...roomData,
            joined: true,
            joinerName: userProfile?.name || 'রাহুল আহমেদ',
            joinerAvatar: userProfile?.avatar || '🧔',
            joinerAvatarColor: 'from-pink-500 to-rose-500',
            joinerAvatarLetter: userProfile?.name ? getAvatarLetter(userProfile.name) : 'রা',
            joinerBatch: 'SSC 2027'
          };
          localStorage.setItem(roomKey, JSON.stringify(updatedData));
        } catch (e) {
          console.error("Error handling joiner sync:", e);
        }
      } else {
        // Fallback room creation if the key doesn't exist
        const fallbackData = {
          creatorName: 'Shajjad Islam',
          creatorAvatar: '👱‍♂️',
          creatorAvatarColor: 'from-cyan-400 to-blue-500',
          creatorAvatarLetter: 'শা',
          creatorBatch: 'SSC 2027',
          joined: true,
          joinerName: userProfile?.name || 'রাহুল আহমেদ',
          joinerAvatar: userProfile?.avatar || '🧔',
          joinerAvatarColor: 'from-pink-500 to-rose-500',
          joinerAvatarLetter: userProfile?.name ? getAvatarLetter(userProfile.name) : 'রা',
          joinerBatch: 'SSC 2027'
        };
        localStorage.setItem(roomKey, JSON.stringify(fallbackData));
      }
      
      setOpponent({
        name: creatorName,
        avatar: creatorAvatar,
        avatarColor: creatorAvatarColor,
        avatarLetter: creatorAvatarLetter,
        batch: creatorBatch,
        online: true,
        accuracy: '95%'
      });
      setPhase('vs');
    }
  }, [userProfile]);

  // VS Screen transition effect
  useEffect(() => {
    if (phase !== 'vs') return;

    const transitionTimer = setTimeout(() => {
      setPhase('game');
    }, 3000);

    return () => clearTimeout(transitionTimer);
  }, [phase]);

  // Gameplay timer effect
  useEffect(() => {
    if (phase !== 'game') return;

    // Reset for new question
    setTimer(15);
    setUserSelectedOption(null);
    setOpponentSelectedOption(null);
    setShowFeedback(false);
    setUserRead(false);
    setOpponentRead(false);

    // User reads after 1.5s
    const userReadTimeout = setTimeout(() => {
      setUserRead(true);
    }, 1500);

    // Opponent reads after a random delay (1.2s to 2.2s)
    const opponentReadTimeout = setTimeout(() => {
      setOpponentRead(true);
    }, 1200 + Math.random() * 1000);

    // Start timer interval
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          revealAnswers(userSelectedOptionRef.current, opponentSelectedOptionRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Simulate opponent response
    const opponentDelay = Math.random() * 5000 + 2000; // 2s - 7s delay
    const opponentTimer = setTimeout(() => {
      if (phase === 'game' && !showFeedback) {
        const currentQ = activeQuestions[currentQuestionIndex];
        const correctIndex = parseInt(currentQ.correct_answer);
        
        // Scholar bot logic: 80% accuracy
        const isCorrect = Math.random() < 0.8;
        let chosen = correctIndex;
        if (!isCorrect) {
          const wrongOptions = [0, 1, 2, 3].filter(i => i !== correctIndex);
          chosen = wrongOptions[Math.floor(Math.random() * wrongOptions.length)];
        }
        setOpponentSelectedOption(chosen);
      }
    }, opponentDelay);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      clearTimeout(opponentTimer);
      clearTimeout(userReadTimeout);
      clearTimeout(opponentReadTimeout);
    };
  }, [phase, currentQuestionIndex]);

  // Reveal answers immediately when both players make a choice
  useEffect(() => {
    if (phase !== 'game') return;
    if (userSelectedOption !== null && opponentSelectedOption !== null && !showFeedback) {
      if (timerRef.current) clearInterval(timerRef.current);
      revealAnswers(userSelectedOption, opponentSelectedOption);
    }
  }, [phase, userSelectedOption, opponentSelectedOption, showFeedback]);

  // Handle user answer submission
  const handleUserAnswer = (index: number) => {
    if (userSelectedOption !== null || showFeedback) return;
    setUserSelectedOption(index);
  };

  // Helper to reveal correct/incorrect feedback
  const revealAnswers = (userChoice: number | null, opponentChoice: number | null) => {
    const currentQ = activeQuestions[currentQuestionIndex];
    const correctIndex = parseInt(currentQ.correct_answer);

    // User score calculation
    if (userChoice !== null) {
      if (userChoice === correctIndex) {
        setUserScore(prev => prev + 1); // exactly 1 point!
      }
    }

    // Opponent score calculation
    const finalOpponentChoice = opponentChoice !== null ? opponentChoice : 
      (Math.random() < 0.8 ? correctIndex : (correctIndex + 1) % 4); // quick fallback choice if timeout

    if (finalOpponentChoice !== null) {
      if (finalOpponentChoice === correctIndex) {
        setOpponentScore(prev => prev + 1); // exactly 1 point!
      }
    }
    setOpponentSelectedOption(finalOpponentChoice);
    
    // Store in history
    setUserAnswersHistory(prev => ({ ...prev, [currentQuestionIndex]: userChoice }));
    setOpponentAnswersHistory(prev => ({ ...prev, [currentQuestionIndex]: finalOpponentChoice }));

    setShowFeedback(true);

    // Transition to next question or results
    setTimeout(() => {
      if (currentQuestionIndex < activeQuestions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        setPhase('result');
        // Calculate who won with the newly calculated scores
        let finalUserScore = userScore;
        let finalOpponentScore = opponentScore;
        if (userChoice !== null && userChoice === correctIndex) {
          finalUserScore += 1;
        }
        if (finalOpponentChoice !== null && finalOpponentChoice === correctIndex) {
          finalOpponentScore += 1;
        }
        triggerConfetti(finalUserScore >= finalOpponentScore);
      }
    }, 3000);
  };

  const renderUserStatus = () => {
    const currentQ = activeQuestions[currentQuestionIndex];
    const correctIndex = parseInt(currentQ.correct_answer);

    if (showFeedback) {
      if (userSelectedOption === correctIndex) {
        return (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex-shrink-0 font-bold">
            ✓
          </span>
        );
      } else {
        return (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex-shrink-0 font-bold">
            ✕
          </span>
        );
      }
    }

    if (userSelectedOption !== null) {
      return <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse flex-shrink-0" />;
    }
    return <span className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-slate-600 flex-shrink-0" />;
  };

  const renderOpponentStatus = () => {
    const currentQ = activeQuestions[currentQuestionIndex];
    const correctIndex = parseInt(currentQ.correct_answer);

    if (showFeedback) {
      if (opponentSelectedOption === correctIndex) {
        return (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex-shrink-0 font-bold">
            ✓
          </span>
        );
      } else {
        return (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex-shrink-0 font-bold">
            ✕
          </span>
        );
      }
    }

    if (opponentSelectedOption !== null) {
      return <span className="w-2.5 h-2.5 rounded-full bg-pink-400 animate-pulse flex-shrink-0" />;
    }
    return <span className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-slate-600 flex-shrink-0" />;
  };

  // Send quick sticker/chat
  const handleSendSticker = (stickerText: string) => {
    setUserBubble(stickerText);
    setShowStickerTray(false);

    // Clear bubble after 3 seconds
    setTimeout(() => setUserBubble(null), 3000);

    // Simulate opponent response sticker
    setTimeout(() => {
      const response = responseMapping[stickerText] || { text: "ধন্যবাদ!", emoji: "👍" };
      setOpponentBubble(`${response.emoji} ${response.text}`);
      setTimeout(() => setOpponentBubble(null), 3000);
    }, 1500);
  };

  const triggerConfetti = (won: boolean) => {
    if (won) {
      setShowConfetti(true);
    }
  };

  return (
    <div className={`relative w-full min-h-[500px] md:min-h-[680px] flex flex-col ${isDarkMode ? 'bg-brand-dark-bg text-white' : 'bg-white text-slate-800'} rounded-3xl overflow-hidden border ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
      {/* Zero-Dependency Confetti Particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-50">
          {Array.from({ length: 80 }).map((_, i) => {
            const randomX = Math.random() * 100;
            const randomDelay = Math.random() * 2;
            const randomDuration = Math.random() * 3 + 2;
            const randomSize = Math.random() * 10 + 6;
            const colors = ['#00CBA9', '#6366F1', '#EC4899', '#F59E0B', '#EF4444', '#10B981'];
            const randomColor = colors[Math.floor(Math.random() * colors.length)];

            return (
              <motion.div
                key={i}
                initial={{ 
                  opacity: 1, 
                  y: -20, 
                  x: `${randomX}vw`, 
                  rotate: 0,
                  scale: 1 
                }}
                animate={{ 
                  y: "110vh", 
                  rotate: Math.random() * 720 - 360,
                  opacity: [1, 1, 0.4, 0] 
                }}
                transition={{ 
                  duration: randomDuration, 
                  delay: randomDelay,
                  ease: "easeOut"
                }}
                style={{
                  position: 'absolute',
                  width: randomSize,
                  height: randomSize,
                  borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                  backgroundColor: randomColor,
                }}
              />
            );
          })}
        </div>
      )}
      
      {/* 2.1 PLAYER VS PLAYER LOBBY */}
      {phase === 'lobby' && (
        <div className="flex-1 flex flex-col max-w-lg mx-auto w-full px-6 py-8 justify-between relative overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between">
            <button 
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors border-none cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h3 className={`font-bangla text-lg font-extrabold tracking-wide ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>চ্যালেঞ্জ</h3>
              <p className={`font-bangla text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{subjectName} • {chapterName}</p>
            </div>
            <div className="w-9" />
          </div>

          {/* Central Pulsing Swords Shield */}
          <div className="flex flex-col items-center justify-center my-8">
            <motion.div
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, -3, 3, 0]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 4,
                ease: "easeInOut"
              }}
              className={`w-36 h-36 rounded-full flex items-center justify-center relative shadow-lg ${isDarkMode ? 'bg-indigo-500/10 shadow-indigo-500/10' : 'bg-indigo-50 shadow-indigo-500/5'}`}
            >
              <div className="w-28 h-28 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-500 flex items-center justify-center relative z-10 border border-indigo-400/20">
                <Shield className="w-14 h-14 text-white drop-shadow-[0_4px_10px_rgba(0,0,0,0.3)]" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 15, ease: "linear" }}
                    className="absolute w-24 h-24 border border-dashed border-white/20 rounded-full"
                  />
                </div>
              </div>
              <div className="absolute inset-0 bg-indigo-500/5 blur-2xl rounded-full scale-125" />
            </motion.div>
          </div>

          {/* Classmates Active Grid */}
          <div className="space-y-3 flex-1 flex flex-col justify-center">
            <p className={`font-bangla text-xs text-center font-bold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>অনলাইন সহপাঠীবৃন্দ</p>
            <motion.div 
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: { staggerChildren: 0.05 }
                }
              }}
              initial="hidden"
              animate="show"
              className="grid grid-cols-3 gap-3"
            >
              {lobbyClassmates.map((classmate, index) => (
                <motion.div 
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 15 },
                    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
                  }}
                  className={`border rounded-2xl p-3 flex flex-col items-center justify-between text-center relative transition-all ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10 text-white' : 'bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-800'}`}
                >
                  {/* Image 2 circular letter avatar style */}
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-tr ${classmate.avatarColor} flex items-center justify-center text-white font-bold text-base shadow-sm font-bangla mb-1.5`}>
                    {classmate.avatarLetter}
                  </div>

                  <span className={`font-bangla text-[11px] font-extrabold truncate max-w-full leading-tight ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{classmate.name}</span>
                  <span className="font-bangla text-[9px] text-slate-400 font-semibold mt-0.5 leading-none">{classmate.batch}</span>
                  
                  {classmate.online ? (
                    <span className={`mt-1.5 px-1.5 py-0.5 rounded font-bangla text-[9px] font-extrabold flex items-center gap-1 ${isDarkMode ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400' : 'bg-emerald-50 border border-emerald-350 text-emerald-600'}`}>
                      <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                      {classmate.accuracy}
                    </span>
                  ) : (
                    <span className={`mt-1.5 text-[9px] font-bangla font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      🕒 {classmate.activeTime}
                    </span>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Action buttons */}
          <div className="space-y-3 mt-8">
            <button
              onClick={() => setPhase('matchmaking')}
              className="w-full py-4 rounded-2xl bg-[#00CBA9] text-white font-bangla font-extrabold text-sm hover:bg-[#00B097] transition-all transform active:scale-[0.98] shadow-lg shadow-emerald-500/10 cursor-pointer border-none"
            >
              ব্যাটল শুরু করো
            </button>
            <button
              onClick={() => setPhase('invite')}
              className={`w-full py-4 rounded-2xl border font-bangla font-bold text-sm transition-all cursor-pointer ${isDarkMode ? 'bg-white/5 text-white border-white/10 hover:bg-white/10' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
            >
              বন্ধুর সাথে খেলো
            </button>
          </div>
        </div>
      )}

      {/* 2.15 WAITING FOR FRIEND (INVITE PHASE) */}
      {phase === 'invite' && (
        <div className="flex-1 flex flex-col max-w-lg mx-auto w-full px-6 py-8 justify-between relative overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setPhase('lobby')}
              className={`p-2 rounded-xl transition-colors border-none cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h3 className={`font-bangla text-lg font-extrabold tracking-wide ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>চ্যালেঞ্জ</h3>
              <p className={`font-bangla text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{subjectName} • {chapterName}</p>
            </div>
            <div className="w-9" />
          </div>

          {/* Central Radar Matchmaking Screen (Image 2/3 of current prompt) */}
          <div className="flex-grow flex flex-col items-center justify-center my-auto relative">
            {/* Concentric Pulsing Radar Rings */}
            <div className="relative w-80 h-80 flex items-center justify-center">
              {/* Outer Pulse Ring 3 */}
              <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className={`absolute w-72 h-72 rounded-full border border-dashed ${isDarkMode ? 'border-purple-500/25' : 'border-purple-500/40'}`}
              />
              
              {/* Middle Pulse Ring 2 */}
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.8, 0.4] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                className={`absolute w-56 h-56 rounded-full border-2 ${isDarkMode ? 'border-purple-500/35' : 'border-purple-500/50'}`}
              />
              
              {/* Inner Pulse Ring 1 */}
              <motion.div
                animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.9, 0.5] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className={`absolute w-40 h-40 rounded-full border-2 ${isDarkMode ? 'border-purple-500/50' : 'border-purple-500/70'}`}
              />

              {/* Central User Avatar (Actual user profile avatar!) */}
              <div className="absolute w-24 h-24 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 flex items-center justify-center shadow-[0_0_40px_rgba(168,85,247,0.5)] z-20 border-4 border-purple-500 text-white font-bold text-3xl font-bangla overflow-hidden">
                {userProfile?.avatar || (window as any).shAuth?.user?.avatar ? (
                  <img src={userProfile?.avatar || (window as any).shAuth?.user?.avatar} alt="User" className="w-full h-full object-cover" />
                ) : (
                  (window as any).shAuth?.user?.name ? getAvatarLetter((window as any).shAuth?.user?.name) : 'শা'
                )}
              </div>

              {/* Orbiting Classmate Avatars at different angles */}
              {lobbyClassmates.slice(0, 4).map((classmate, idx) => {
                const angles = [45, 135, 225, 315];
                const radius = 105;
                const angleRad = (angles[idx] * Math.PI) / 180;
                const x = Math.cos(angleRad) * radius;
                const y = Math.sin(angleRad) * radius;

                return (
                  <motion.div
                    key={idx}
                    initial={{ scale: 0, x: 0, y: 0 }}
                    animate={{ scale: 1, x, y }}
                    transition={{ type: "spring", stiffness: 100, delay: idx * 0.3 }}
                    className={`absolute w-11 h-11 rounded-full border-2 flex items-center justify-center shadow-lg z-10 text-xs font-bold text-white bg-gradient-to-tr ${classmate.avatarColor} ${isDarkMode ? 'border-white/50' : 'border-slate-200'}`}
                  >
                    {classmate.avatarLetter}
                  </motion.div>
                );
              })}
            </div>

            {/* Waiting Status text below radar */}
            <div className="text-center space-y-2 mt-6">
              <h4 className={`font-bangla text-base font-extrabold tracking-wide ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Waiting for friend</h4>
              <p className={`font-mono text-sm font-bold ${isDarkMode ? 'text-[#A0A5AD]' : 'text-slate-500'}`}>{formatInviteTime(inviteTimer)}</p>
            </div>
          </div>

          {/* Bottom Link Copy Area */}
          <div className="space-y-4 pb-6">
            <p className={`font-bangla text-xs text-center font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              চর্চার বন্ধুকে ইনভাইট করো
            </p>
            <div className={`flex gap-2 rounded-2xl p-1.5 items-center border ${isDarkMode ? 'bg-black/40 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <input
                type="text"
                readOnly
                value={`${window.location.origin}${window.location.pathname}?battle=${inviteCode}`}
                className={`flex-1 bg-transparent border-none text-xs font-mono px-3 py-2 outline-none focus:ring-0 select-all ${isDarkMode ? 'text-white' : 'text-slate-800'}`}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?battle=${inviteCode}`);
                  alert("ইনভাইট লিংক কপি করা হয়েছে!");
                }}
                className="py-2.5 px-4 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bangla text-xs font-extrabold transition-all flex items-center gap-1.5 border-none cursor-pointer"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2.2 MATCHMAKING / FIND MATCH */}
      {phase === 'matchmaking' && (
        <div className="flex-1 flex flex-col max-w-lg mx-auto w-full px-6 py-8 justify-between">
          <div className="flex items-center justify-start">
            <button 
              onClick={() => setPhase('lobby')}
              className={`p-2 rounded-xl transition-colors border-none cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-col items-center text-center space-y-6 my-auto relative">
            {/* Concentric Pulsing Radar Rings */}
            <div className="relative w-80 h-80 flex items-center justify-center">
              {/* Outer Pulse Ring 3 */}
              <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className={`absolute w-72 h-72 rounded-full border border-dashed ${isDarkMode ? 'border-purple-500/25' : 'border-purple-500/40'}`}
              />
              
              {/* Middle Pulse Ring 2 */}
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.8, 0.4] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                className={`absolute w-56 h-56 rounded-full border-2 ${isDarkMode ? 'border-purple-500/35' : 'border-purple-500/50'}`}
              />
              
              {/* Inner Pulse Ring 1 */}
              <motion.div
                animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.9, 0.5] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className={`absolute w-40 h-40 rounded-full border-2 ${isDarkMode ? 'border-purple-500/50' : 'border-purple-500/70'}`}
              />

              {/* Central User Avatar */}
              <div className="absolute w-24 h-24 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 flex items-center justify-center shadow-[0_0_40px_rgba(168,85,247,0.5)] z-20 border-4 border-purple-500 text-white font-bold text-3xl font-bangla overflow-hidden">
                {userProfile?.avatar || (window as any).shAuth?.user?.avatar ? (
                  <img src={userProfile?.avatar || (window as any).shAuth?.user?.avatar} alt="User" className="w-full h-full object-cover" />
                ) : (
                  (window as any).shAuth?.user?.name ? getAvatarLetter((window as any).shAuth?.user?.name) : 'শা'
                )}
              </div>

              {/* Orbiting Classmate Avatars at different angles */}
              {lobbyClassmates.slice(0, 4).map((classmate, idx) => {
                const angles = [45, 135, 225, 315];
                const radius = 105;
                const angleRad = (angles[idx] * Math.PI) / 180;
                const x = Math.cos(angleRad) * radius;
                const y = Math.sin(angleRad) * radius;

                return (
                  <motion.div
                    key={idx}
                    initial={{ scale: 0, x: 0, y: 0 }}
                    animate={{ scale: 1, x, y }}
                    transition={{ type: "spring", stiffness: 100, delay: idx * 0.3 }}
                    className={`absolute w-11 h-11 rounded-full border-2 flex items-center justify-center shadow-lg z-10 text-xs font-bold text-white bg-gradient-to-tr ${classmate.avatarColor} ${isDarkMode ? 'border-white/50' : 'border-slate-200'}`}
                  >
                    {classmate.avatarLetter}
                  </motion.div>
                );
              })}
            </div>
            
            <div className="space-y-2 relative z-10">
              <h3 className={`font-bangla text-lg font-extrabold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>প্রতিদ্বন্দ্বী খোঁজা হচ্ছে...</h3>
              <p className={`font-bangla text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{subjectName} • {chapterName}</p>
              <p className={`font-bangla text-xs font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>{toBengaliNumber(notifiedCount)} জন নোটিফিকেশন পেয়েছে</p>
            </div>
            
            <div className={`px-4 py-2 rounded-full font-bangla text-sm text-amber-500 font-bold border relative z-10 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              বাকি সময়: {toBengaliNumber(matchmakingTimer)} সেকেন্ড
            </div>
          </div>

          <button
            onClick={() => setPhase('lobby')}
            className={`w-full py-4 rounded-2xl border font-bangla font-bold text-sm transition-all cursor-pointer ${isDarkMode ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/20' : 'bg-rose-50 hover:bg-rose-100 text-rose-600 border-rose-200'}`}
          >
            বাতিল করুন
          </button>
        </div>
      )}

      {/* 2.18 BATTLE MATCH VS TRANSITION SCREEN (Image 4/5 of current PRD) */}
      {phase === 'vs' && opponent && (
        <div className="flex-grow flex flex-col justify-between max-w-lg mx-auto w-full px-6 py-12 relative overflow-hidden bg-gradient-to-br from-purple-700 via-indigo-800 to-slate-900">
          {/* Top Left Close/Back Arrow */}
          <div className="flex items-center justify-start z-20">
            <button 
              onClick={() => setPhase('lobby')}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border-none text-white cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>

          {/* Central VS Layout with Speed Lines & Stars */}
          <div className="relative flex-grow flex flex-col items-center justify-center my-auto">
            {/* Speed Lines Overlay Background */}
            <div className="absolute inset-0 opacity-10 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.1)_50%,rgba(255,255,255,0.1)_75%,transparent_75%,transparent)] bg-[length:40px_40px] animate-pulse" />
            
            {/* Floating Stars */}
            <div className="absolute w-full h-full pointer-events-none">
              <motion.span animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="absolute top-10 left-10 text-yellow-300 text-lg">⭐</motion.span>
              <motion.span animate={{ y: [0, -15, 0] }} transition={{ repeat: Infinity, duration: 2.5 }} className="absolute bottom-20 left-20 text-yellow-300 text-sm">⭐</motion.span>
              <motion.span animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="absolute top-20 right-10 text-yellow-300 text-base">⭐</motion.span>
              <motion.span animate={{ y: [0, -12, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="absolute bottom-10 right-20 text-yellow-300 text-xs">⭐</motion.span>
            </div>

            {/* Players Visual Matchup Row */}
            <div className="flex items-center justify-between w-full max-w-sm px-4 relative z-10">
              {/* Player 1 (YOU) */}
              <motion.div
                initial={{ opacity: 0, x: -60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 80, delay: 0.2 }}
                className="flex flex-col items-center space-y-3"
              >
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 border-4 border-emerald-400 flex items-center justify-center text-white font-bold text-3xl shadow-xl shadow-emerald-400/20 font-bangla overflow-hidden">
                    {userProfile?.avatar || (window as any).shAuth?.user?.avatar ? (
                      <img src={userProfile?.avatar || (window as any).shAuth?.user?.avatar} alt="User" className="w-full h-full object-cover" />
                    ) : (
                      (window as any).shAuth?.user?.name ? getAvatarLetter((window as any).shAuth?.user?.name) : 'শা'
                    )}
                  </div>
                  {/* YOU badge on top */}
                  <span className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-yellow-400 border border-slate-900 text-slate-900 font-bangla font-black text-[9px] shadow-md uppercase">
                    YOU
                  </span>
                </div>
                <span className="font-bangla text-xs font-black text-white bg-slate-900/60 px-3 py-1.5 rounded-full border border-white/5 shadow-sm">
                  {(window as any).shAuth?.user?.name || 'Shajjad Islam'}
                </span>
              </motion.div>

              {/* Glowing VS Logo in Middle */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 120, delay: 0.5 }}
                className="flex flex-col items-center"
              >
                <h1 className="text-6xl md:text-7xl font-black italic tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-200 to-white drop-shadow-[0_4px_12px_rgba(234,179,8,0.5)] select-none">
                  VS
                </h1>
              </motion.div>

              {/* Player 2 (Opponent) */}
              <motion.div
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 80, delay: 0.2 }}
                className="flex flex-col items-center space-y-3"
              >
                <div className="relative">
                  <div className={`w-24 h-24 rounded-full bg-gradient-to-tr ${opponent.avatarColor || 'from-pink-500 to-rose-500'} border-4 border-pink-500 flex items-center justify-center text-white font-bold text-3xl shadow-xl shadow-pink-500/20 font-bangla`}>
                    {opponent.avatarLetter || getAvatarLetter(opponent.name)}
                  </div>
                </div>
                <span className="font-bangla text-xs font-black text-white bg-slate-900/60 px-3 py-1.5 rounded-full border border-white/5 shadow-sm">
                  {opponent.name}
                </span>
              </motion.div>
            </div>
          </div>

          {/* Bottom decorative bar */}
          <div className="h-2 w-12 bg-white/20 rounded-full mx-auto self-center mt-4" />
        </div>
      )}

      {/* 2.3 REAL-TIME ARENA GAMEPLAY */}
      {phase === 'game' && opponent && (() => {
        const currentQ = activeQuestions[currentQuestionIndex];
        const correctIndex = parseInt(currentQ.correct_answer);

        return (
          <div className={`flex-grow flex flex-col justify-between w-full relative ${isDarkMode ? 'bg-brand-dark-bg' : 'bg-white'}`}>
            
            {/* Top-most full-width green progress bar (Image 2) */}
            <div className={`w-full h-1.5 overflow-hidden relative ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <motion.div 
                initial={{ width: "100%" }}
                animate={{ width: `${(timer / 15) * 100}%` }}
                transition={{ duration: 1, ease: "linear" }}
                className="bg-[#00E575] h-full"
              />
            </div>

            {/* Header: X button left-aligned (Image 2), timer right-aligned */}
            <div className="flex items-center justify-between px-4 pt-3 md:px-6 md:pt-4">
              <button 
                onClick={onClose} 
                className={`p-1 rounded-lg transition-colors border-none bg-transparent cursor-pointer ${isDarkMode ? 'hover:bg-white/5 text-white/80 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-800'}`}
              >
                <X className="w-5 h-5" />
              </button>
              <span className="font-bangla text-xs font-bold text-amber-500">
                {toBengaliNumber(timer)} সেকেন্ড বাকি
              </span>
            </div>

            {/* Question Text (Image 2: Left-aligned, no border box, clean font) */}
            <div className="px-4 py-3 md:px-6 md:py-5 text-left">
              <h2 className={`font-bangla text-sm md:text-base font-medium leading-relaxed ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {currentQ.question_text}
              </h2>
            </div>

            {/* MCQ Options grid (Image 2: centered text, clean card backgrounds) */}
            <div className="px-4 space-y-2.5 md:px-6 md:space-y-3 mb-auto">
              {currentQ.options.map((option: string, oIdx: number) => {
                const isSelected = userSelectedOption === oIdx;
                const isCorrect = oIdx === correctIndex;
                
                let btnStyle = "";
                if (isDarkMode) {
                  btnStyle = "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-slate-650";
                  if (showFeedback) {
                    if (isCorrect) {
                       btnStyle = "bg-emerald-500/20 border-emerald-500 text-emerald-400 font-extrabold";
                    } else if (isSelected) {
                       btnStyle = "bg-rose-500/20 border-rose-500 text-rose-400 font-extrabold";
                    } else {
                       btnStyle = "bg-slate-800/40 border-slate-700/30 text-slate-600";
                    }
                  } else if (isSelected) {
                    btnStyle = "bg-[#00CBA9]/20 border-[#00CBA9] text-[#00CBA9] font-bold";
                  }
                } else {
                  btnStyle = "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300";
                  if (showFeedback) {
                    if (isCorrect) {
                      btnStyle = "bg-emerald-50 border-emerald-500 text-emerald-600 font-extrabold";
                    } else if (isSelected) {
                      btnStyle = "bg-rose-50 border-rose-500 text-rose-600 font-extrabold";
                    } else {
                      btnStyle = "bg-slate-50/50 border-slate-150 text-slate-400";
                    }
                  } else if (isSelected) {
                    btnStyle = "bg-[#00CBA9]/10 border-[#00CBA9] text-[#00CBA9] font-bold";
                  }
                }

                return (
                  <button
                    key={oIdx}
                    onClick={() => handleUserAnswer(oIdx)}
                    disabled={showFeedback}
                    className={`w-full py-2.5 md:py-3.5 px-4 md:px-5 rounded-xl border text-center justify-center font-bangla text-xs md:text-sm transition-all flex items-center cursor-pointer border-solid ${btnStyle}`}
                  >
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>

            {/* Live Chat Bubble Stickers drawer overlay */}
            <AnimatePresence>
              {showStickerTray && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowStickerTray(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    className={`absolute bottom-24 right-4 left-4 md:left-auto md:right-6 z-40 border rounded-3xl p-4 shadow-2xl md:max-w-xs space-y-4 ${isDarkMode ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'}`}
                  >
                    <div className={`flex items-center justify-between border-b pb-2 ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
                      <span className={`font-bangla text-xs font-extrabold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>রিয়েল-টাইম চ্যাট</span>
                      <button 
                        onClick={() => setShowStickerTray(false)}
                        className={`p-1 rounded border-none cursor-pointer ${isDarkMode ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {chatStickers.map((sticker, sIdx) => (
                        <button
                          key={sIdx}
                          onClick={() => handleSendSticker(sticker.text)}
                          className={`py-2.5 px-3 rounded-xl border font-bangla text-xs font-bold transition-all text-center cursor-pointer ${isDarkMode ? 'bg-white/5 border-white/10 text-white hover:bg-indigo-500 hover:border-indigo-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600'}`}
                        >
                          {sticker.emoji} {sticker.text}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Transition banner if showing feedback (Image 5 style) */}
            {showFeedback && (
              <div className={`w-full py-3 text-center font-bangla text-xs md:text-sm font-bold flex items-center justify-center gap-2 ${isDarkMode ? 'bg-[#00E575]/10 text-[#00E575] border-t border-b border-[#00E575]/20' : 'bg-emerald-50 text-emerald-700 border-t border-b border-emerald-100'}`}>
                <span className="w-2 h-2 rounded-full bg-[#00E575] animate-ping" />
                <span>পরবর্তী প্রশ্নে যাওয়া হচ্ছে</span>
              </div>
            )}

            {/* Bottom Status bar with Scores (Image 2: Pills & Chat bubble button) */}
            <div className={`border-t py-2 px-2.5 md:py-3.5 md:px-6 flex items-center justify-between gap-2 md:gap-2.5 relative mt-3 md:mt-6 ${isDarkMode ? 'border-slate-800 bg-slate-950/40' : 'border-slate-200 bg-slate-50/80'}`}>
              
              {/* Left Pill (User Stats Card) */}
              <div className={`flex-grow flex items-center gap-1.5 md:gap-2.5 border px-2 py-1.5 md:py-2 md:px-4 rounded-xl md:rounded-2xl shadow-sm max-w-[130px] sm:max-w-[170px] md:max-w-xs relative font-bangla transition-all duration-305 ${
                showFeedback 
                  ? (isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-850')
                  : (userSelectedOption !== null 
                      ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-900/30 text-[#4F46E5] dark:text-indigo-400' 
                      : 'bg-white border-slate-200 dark:bg-slate-900 dark:border-zinc-800 text-slate-700 dark:text-slate-350')
              }`}>
                {/* Avatar */}
                <div className="w-6.5 h-6.5 md:w-8 md:h-8 rounded-full overflow-hidden border border-slate-200 dark:border-zinc-700 flex-shrink-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                  {userProfile?.avatar || (window as any).shAuth?.user?.avatar ? (
                    <img src={userProfile?.avatar || (window as any).shAuth?.user?.avatar} alt="User" className="w-full h-full object-cover" />
                  ) : (
                    <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Shajjad" alt="User" className="w-full h-full object-cover" />
                  )}
                </div>

                {/* Info Text */}
                <div className="flex flex-col min-w-0 leading-tight text-left">
                  {showFeedback ? (
                    <>
                      <span className="hidden md:inline-block text-xs font-bold truncate max-w-[80px]">{userProfile?.name || 'Shajjad Islam'}</span>
                      <span className="text-[10px] md:text-xs font-extrabold flex items-center gap-1">
                        {renderUserStatus()}
                        <span>{toBengaliNumber(userScore)} পয়েন্ট</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="hidden md:inline-block text-[10px] text-slate-400 dark:text-zinc-500 font-semibold">{userProfile?.name || 'Shajjad Islam'}</span>
                      <span className="text-[10px] md:text-xs font-extrabold whitespace-nowrap">
                        {userSelectedOption !== null ? 'উত্তর দিয়েছে' : (userRead ? 'প্রশ্ন পড়েছে।' : 'প্রশ্ন পড়ছে...')}
                      </span>
                    </>
                  )}
                </div>
                
                {/* User Sticker Bubble */}
                <AnimatePresence>
                  {userBubble && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: -45 }}
                      exit={{ opacity: 0, scale: 0.8, y: -10 }}
                      className="absolute left-3 bg-[#00CBA9] text-white px-2.5 py-1 rounded-xl rounded-bl-none text-[11px] font-bangla font-extrabold shadow-lg z-50 after:content-[''] after:absolute after:top-full after:left-3 after:border-[6px] after:border-transparent after:border-t-[#00CBA9]"
                    >
                      {userBubble}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Right Pill (Opponent Stats Card) */}
              <div className={`flex-grow flex items-center gap-1.5 md:gap-2.5 border px-2 py-1.5 md:py-2 md:px-4 rounded-xl md:rounded-2xl shadow-sm max-w-[130px] sm:max-w-[170px] md:max-w-xs relative font-bangla transition-all duration-305 ${
                showFeedback 
                  ? (isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-850')
                  : (opponentSelectedOption !== null 
                      ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-900/30 text-[#4F46E5] dark:text-indigo-400' 
                      : 'bg-white border-slate-200 dark:bg-slate-900 dark:border-zinc-800 text-slate-700 dark:text-slate-350')
              }`}>
                {/* Avatar */}
                <div className="w-6.5 h-6.5 md:w-8 md:h-8 rounded-full overflow-hidden border border-slate-200 dark:border-zinc-700 flex-shrink-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                  {opponent.avatar ? (
                    <img src={opponent.avatar} alt="Opponent" className="w-full h-full object-cover" />
                  ) : (
                    <img src="https://api.dicebear.com/7.x/adventurer/svg?seed=Tasmiya" alt="Opponent" className="w-full h-full object-cover" />
                  )}
                </div>

                {/* Info Text */}
                <div className="flex flex-col min-w-0 leading-tight text-left">
                  {showFeedback ? (
                    <>
                      <span className="hidden md:inline-block text-xs font-bold truncate max-w-[80px]">{opponent.name}</span>
                      <span className="text-[10px] md:text-xs font-extrabold flex items-center gap-1">
                        {renderOpponentStatus()}
                        <span>{toBengaliNumber(opponentScore)} পয়েন্ট</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="hidden md:inline-block text-[10px] text-slate-400 dark:text-zinc-500 font-semibold">{opponent.name}</span>
                      <span className="text-[10px] md:text-xs font-extrabold whitespace-nowrap">
                        {opponentSelectedOption !== null ? 'উত্তর দিয়েছে' : (opponentRead ? 'প্রশ্ন পড়েছে।' : 'প্রশ্ন পড়ছে...')}
                      </span>
                    </>
                  )}
                </div>

                {/* Opponent Sticker Bubble */}
                <AnimatePresence>
                  {opponentBubble && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: -45 }}
                      exit={{ opacity: 0, scale: 0.8, y: -10 }}
                      className="absolute right-3 bg-[#EC4899] text-white px-2.5 py-1 rounded-xl rounded-br-none text-[11px] font-bangla font-extrabold shadow-lg z-50 after:content-[''] after:absolute after:top-full after:right-3 after:border-[6px] after:border-transparent after:border-t-[#EC4899]"
                    >
                      {opponentBubble}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Dummy container for trailing cleanup */}
              <div className="hidden">
                
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-2 min-w-0 text-right">
                    <span className={`font-bangla text-[10px] md:text-xs font-extrabold flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>{toBengaliNumber(opponentScore)} পয়েন্ট</span>
                    <span className={`hidden md:inline-block font-bangla text-xs font-bold truncate max-w-[80px] ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{opponent.name}</span>
                  </div>
                  <div className={`w-7 h-7 rounded-full bg-gradient-to-tr ${opponent.avatarColor || 'from-pink-500 to-rose-500'} flex items-center justify-center text-white font-bold text-xs border border-pink-500/30 flex-shrink-0 font-bangla`}>
                    {opponent.avatarLetter || getAvatarLetter(opponent.name)}
                  </div>
                </div>

                {/* Opponent Sticker Bubble */}
                <AnimatePresence>
                  {opponentBubble && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: -45 }}
                      exit={{ opacity: 0, scale: 0.8, y: -10 }}
                      className="absolute right-3 bg-[#EC4899] text-white px-2.5 py-1 rounded-xl rounded-br-none text-[11px] font-bangla font-extrabold shadow-lg z-50 after:content-[''] after:absolute after:top-full after:right-3 after:border-[6px] after:border-transparent after:border-t-[#EC4899]"
                    >
                      {opponentBubble}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Chat action button on far right */}
              <button
                onClick={() => setShowStickerTray(!showStickerTray)}
                className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#EC4899] hover:bg-pink-600 flex items-center justify-center transition-all cursor-pointer text-white shadow-md shadow-pink-500/20 border-none flex-shrink-0"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            </div>

          </div>
        );
      })()}
      {/* 2.4 POST-MATCH REWARDS */}
      {phase === 'result' && opponent && (() => {
        const userCorrectCount = activeQuestions.reduce((acc: number, q: any, idx: number) => {
          const correctIndex = parseInt(q.correct_answer);
          return acc + (userAnswersHistory[idx] === correctIndex ? 1 : 0);
        }, 0);

        const userWrongCount = activeQuestions.reduce((acc: number, q: any, idx: number) => {
          const correctIndex = parseInt(q.correct_answer);
          const ans = userAnswersHistory[idx];
          return acc + (ans !== null && ans !== correctIndex ? 1 : 0);
        }, 0);

        const userSkippedCount = activeQuestions.reduce((acc: number, _q: any, idx: number) => {
          return acc + (userAnswersHistory[idx] === null ? 1 : 0);
        }, 0);

        const opponentCorrectCount = activeQuestions.reduce((acc: number, q: any, idx: number) => {
          const correctIndex = parseInt(q.correct_answer);
          return acc + (opponentAnswersHistory[idx] === correctIndex ? 1 : 0);
        }, 0);

        const opponentWrongCount = activeQuestions.reduce((acc: number, q: any, idx: number) => {
          const correctIndex = parseInt(q.correct_answer);
          const ans = opponentAnswersHistory[idx];
          return acc + (ans !== null && ans !== correctIndex ? 1 : 0);
        }, 0);

        return (
          <div className="flex-grow flex flex-col w-full relative overflow-y-auto max-h-[85vh]">
            {/* Header: Back arrow & Chapter Title */}
            <div className={`px-6 py-4 flex items-center gap-4 border-b ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
              <button 
                onClick={onClose}
                className={`p-2 rounded-xl transition-colors border-none cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className={`font-bangla text-base font-extrabold truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {chapterName}
              </h2>
            </div>

            {/* Statistics capsules bar */}
            <div className="flex items-center justify-center gap-3 px-6 py-4 flex-wrap">
              <span className={`px-4 py-2 rounded-full font-bangla text-xs font-bold border flex items-center gap-1.5 ${isDarkMode ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {toBengaliNumber(userCorrectCount)} সঠিক
              </span>
              <span className={`px-4 py-2 rounded-full font-bangla text-xs font-bold border flex items-center gap-1.5 ${isDarkMode ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' : 'bg-rose-50 border-rose-100 text-rose-700'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                {toBengaliNumber(userWrongCount)} ভুল
              </span>
              <span className={`px-4 py-2 rounded-full font-bangla text-xs font-bold border flex items-center gap-1.5 ${isDarkMode ? 'bg-slate-500/10 border-slate-500/20 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                {toBengaliNumber(userSkippedCount)} স্কিপ
              </span>
            </div>

            {/* Matchup row */}
            <div className={`mx-6 p-6 rounded-3xl border flex items-center justify-between ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              {/* User Side */}
              <div className="flex flex-col items-center space-y-2 flex-1">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-2xl font-bangla border-2 border-indigo-500/30 overflow-hidden">
                    {userProfile?.avatar || (window as any).shAuth?.user?.avatar ? (
                      <img src={userProfile?.avatar || (window as any).shAuth?.user?.avatar} alt="User" className="w-full h-full object-cover" />
                    ) : (
                      (window as any).shAuth?.user?.name ? getAvatarLetter((window as any).shAuth?.user?.name) : 'শা'
                    )}
                  </div>
                  {userCorrectCount >= opponentCorrectCount && (
                    <span className="absolute -top-1 -right-1 text-base animate-bounce" title="বিজয়ী">👑</span>
                  )}
                </div>
                <div className="text-center">
                  <p className={`font-bangla text-xs font-bold truncate max-w-[100px] ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {(window as any).shAuth?.user?.name || 'তুমি'}
                  </p>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    <span className="text-[10px] font-bangla bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded font-bold">✓ {toBengaliNumber(userCorrectCount)}</span>
                    <span className="text-[10px] font-bangla bg-rose-500/10 text-rose-500 px-1.5 py-0.5 rounded font-bold">✗ {toBengaliNumber(userWrongCount)}</span>
                  </div>
                </div>
              </div>

              {/* VS Badge */}
              <div className="px-3 py-1.5 rounded-full bg-slate-200 dark:bg-white/10 text-[10px] font-black text-slate-500 dark:text-slate-400">
                VS
              </div>

              {/* Opponent Side */}
              <div className="flex flex-col items-center space-y-2 flex-1">
                <div className="relative">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-tr ${opponent.avatarColor || 'from-pink-500 to-rose-500'} flex items-center justify-center text-white font-bold text-2xl font-bangla border-2 border-pink-500/30`}>
                    {opponent.avatarLetter || getAvatarLetter(opponent.name)}
                  </div>
                  {opponentCorrectCount >= userCorrectCount && (
                    <span className="absolute -top-1 -right-1 text-base animate-bounce" title="বিজয়ী">👑</span>
                  )}
                </div>
                <div className="text-center">
                  <p className={`font-bangla text-xs font-bold truncate max-w-[100px] ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {opponent.name}
                  </p>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    <span className="text-[10px] font-bangla bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded font-bold">✓ {toBengaliNumber(opponentCorrectCount)}</span>
                    <span className="text-[10px] font-bangla bg-rose-500/10 text-rose-500 px-1.5 py-0.5 rounded font-bold">✗ {toBengaliNumber(opponentWrongCount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions Review list */}
            <div className="px-6 py-6 space-y-6">
              <h3 className={`font-bangla text-sm font-extrabold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>প্রশ্নের বিশ্লেষণ ও সঠিক উত্তর</h3>
              
              {activeQuestions.map((q: any, qIdx: number) => {
                const correctIdx = parseInt(q.correct_answer);
                const userChosenIdx = userAnswersHistory[qIdx];
                const opponentChosenIdx = opponentAnswersHistory[qIdx];
                const isExpanded = !!expandedExplanations[qIdx];

                return (
                  <div key={qIdx} className={`p-5 rounded-2xl border space-y-4 ${isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)]'}`}>
                    <h4 className={`font-bangla text-sm font-semibold leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                      {toBengaliNumber(qIdx + 1)}. {q.question_text}
                    </h4>

                    <div className="space-y-2.5">
                      {q.options.map((option: string, oIdx: number) => {
                        const isCorrect = oIdx === correctIdx;
                        const isUserChosen = userChosenIdx === oIdx;
                        const isOpponentChosen = opponentChosenIdx === oIdx;

                        let optionStyle = isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-700';
                        if (isCorrect) {
                          optionStyle = isDarkMode ? 'bg-emerald-500/15 border-emerald-500 text-emerald-400 font-extrabold' : 'bg-emerald-50 border-emerald-500 text-emerald-700 font-extrabold';
                        } else if (isUserChosen || isOpponentChosen) {
                          optionStyle = isDarkMode ? 'bg-rose-500/15 border-rose-500 text-rose-400 font-bold' : 'bg-rose-50 border-rose-500 text-rose-600 font-bold';
                        }

                        return (
                          <div key={oIdx} className={`p-4 rounded-xl border flex items-center justify-between gap-3 text-left transition-all ${optionStyle}`}>
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                isCorrect 
                                  ? 'bg-emerald-500 text-white' 
                                  : (isUserChosen || isOpponentChosen) 
                                    ? 'bg-rose-500 text-white' 
                                    : (isDarkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500')
                              }`}>
                                {oIdx === 0 ? 'ক' : oIdx === 1 ? 'খ' : oIdx === 2 ? 'গ' : 'ঘ'}
                              </span>
                              <span className="font-bangla text-xs md:text-sm">{option}</span>
                            </div>

                            {/* Chosen avatars */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {isUserChosen && (
                                <div className="w-5 h-5 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-[8px] font-bangla border border-white overflow-hidden" title="তুমি">
                                  {userProfile?.avatar || (window as any).shAuth?.user?.avatar ? (
                                    <img src={userProfile?.avatar || (window as any).shAuth?.user?.avatar} alt="User" className="w-full h-full object-cover" />
                                  ) : (
                                    (window as any).shAuth?.user?.name ? getAvatarLetter((window as any).shAuth?.user?.name) : 'শা'
                                  )}
                                </div>
                              )}
                              {isOpponentChosen && (
                                <div className={`w-5 h-5 rounded-full bg-gradient-to-tr ${opponent.avatarColor || 'from-pink-500 to-rose-500'} flex items-center justify-center text-white font-bold text-[8px] font-bangla border border-white`} title={opponent.name}>
                                  {opponent.avatarLetter || getAvatarLetter(opponent.name)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation Section */}
                    {q.explanation && (
                      <div className="pt-2">
                        <button
                          onClick={() => setExpandedExplanations(prev => ({ ...prev, [qIdx]: !prev[qIdx] }))}
                          className={`w-full py-2.5 px-4 rounded-xl border flex items-center justify-between font-bangla text-xs font-bold transition-all cursor-pointer ${
                            isDarkMode 
                              ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10' 
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span>💡 ব্যাখ্যা</span>
                          </span>
                          <span className="text-xs transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            ▼
                          </span>
                        </button>
                        
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className={`mt-2 p-4 rounded-xl border font-bangla text-xs leading-relaxed ${
                                isDarkMode 
                                  ? 'bg-slate-900/60 border-white/5 text-slate-300' 
                                  : 'bg-emerald-50/20 border-emerald-100/50 text-slate-600'
                              }`}
                            >
                              {q.explanation}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom Actions */}
            <div className={`p-6 space-y-3 border-t mt-auto ${isDarkMode ? 'bg-brand-dark-bg border-white/10' : 'bg-white border-slate-100'}`}>
              <button
                onClick={() => {
                  setPhase('lobby');
                  setUserScore(0);
                  setOpponentScore(0);
                  setCurrentQuestionIndex(0);
                  setUserAnswersHistory({});
                  setOpponentAnswersHistory({});
                  setExpandedExplanations({});
                  localStorage.removeItem('shuddhoshikha_battle_results');
                }}
                className="w-full py-4 rounded-2xl bg-[#00CBA9] text-white font-bangla font-extrabold text-sm hover:bg-[#00B097] transition-all cursor-pointer border-none"
              >
                আবার খেলুন
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('shuddhoshikha_battle_results');
                  onClose();
                }}
                className={`w-full py-4 rounded-2xl border font-bangla font-bold text-sm transition-all cursor-pointer ${isDarkMode ? 'bg-white/5 text-white border-white/10 hover:bg-white/10' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'}`}
              >
                চর্চাঘর-এ ফিরে যান
              </button>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
